import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  supplierCreateRequestSchema,
  type SupplierCreateRequest,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { ApiError } from '../../lib/api-client';
import { useCreateSupplier, useSupplier, useUpdateSupplier } from '../../lib/suppliers-api';

const DEFAULT_VALUES: SupplierCreateRequest = {
  code: '',
  name: '',
  country: 'IT',
  vatNumber: null,
  address: null,
  email: null,
  phone: null,
  active: true,
};

export function SupplierFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const supplierQ = useSupplier(id);
  const createMut = useCreateSupplier();
  const updateMut = useUpdateSupplier(id ?? '');

  const form = useForm<SupplierCreateRequest>({
    resolver: zodResolver(supplierCreateRequestSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (isEdit && supplierQ.data) {
      form.reset({
        code: supplierQ.data.code,
        name: supplierQ.data.name,
        country: supplierQ.data.country,
        vatNumber: supplierQ.data.vatNumber,
        address: supplierQ.data.address,
        email: supplierQ.data.email,
        phone: supplierQ.data.phone,
        active: supplierQ.data.active,
      });
    }
  }, [isEdit, supplierQ.data, form]);

  async function onSubmit(values: SupplierCreateRequest) {
    // Normalizza: empty strings -> null
    const norm = <T extends string | null | undefined>(v: T): string | null =>
      typeof v === 'string' && v.trim() === '' ? null : (v ?? null);
    const payload: SupplierCreateRequest = {
      ...values,
      vatNumber: norm(values.vatNumber),
      email: norm(values.email),
      phone: norm(values.phone),
      address: values.address && Object.values(values.address).some((v) => v && String(v).trim())
        ? values.address
        : null,
    };
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(payload);
      } else {
        await createMut.mutateAsync(payload);
      }
      navigate('/suppliers');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError('code', { message: t('suppliers.form.errorDuplicateCode') });
      } else {
        window.alert((err as Error).message);
      }
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending;

  if (isEdit && supplierQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && supplierQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('suppliers.form.errorLoad')}: {(supplierQ.error as Error).message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">
          {isEdit ? t('suppliers.form.titleEdit') : t('suppliers.form.titleNew')}
        </h2>
        <Button variant="secondary" onClick={() => navigate('/suppliers')}>
          {t('common.back')}
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('suppliers.form.sectionIdentity')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('suppliers.fields.code')} *</Label>
              <Input id="code" autoComplete="off" {...form.register('code')} />
              {form.formState.errors.code && (
                <p className="text-xs text-red-600">{form.formState.errors.code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('suppliers.fields.name')} *</Label>
              <Input id="name" autoComplete="organization" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-xs text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="country">{t('suppliers.fields.country')} *</Label>
              <Input
                id="country"
                maxLength={2}
                placeholder="IT"
                autoComplete="country"
                {...form.register('country', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                })}
              />
              {form.formState.errors.country && (
                <p className="text-xs text-red-600">{form.formState.errors.country.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vatNumber">{t('suppliers.fields.vatNumber')}</Label>
              <Input
                id="vatNumber"
                placeholder="IT12345678901"
                {...form.register('vatNumber', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                })}
              />
              <p className="text-xs text-slate-500">{t('suppliers.form.vatHint')}</p>
              {form.formState.errors.vatNumber && (
                <p className="text-xs text-red-600">{form.formState.errors.vatNumber.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('suppliers.form.sectionContacts')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('suppliers.fields.email')}</Label>
              <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
              {form.formState.errors.email && (
                <p className="text-xs text-red-600">{form.formState.errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('suppliers.fields.phone')}</Label>
              <Input id="phone" type="tel" autoComplete="tel" {...form.register('phone')} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('suppliers.form.sectionAddress')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="addr-street">{t('suppliers.fields.addressStreet')}</Label>
              <Input id="addr-street" {...form.register('address.street')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-city">{t('suppliers.fields.addressCity')}</Label>
              <Input id="addr-city" {...form.register('address.city')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-postal">{t('suppliers.fields.addressPostalCode')}</Label>
              <Input id="addr-postal" {...form.register('address.postalCode')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-province">{t('suppliers.fields.addressProvince')}</Label>
              <Input id="addr-province" {...form.register('address.province')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-country">{t('suppliers.fields.addressCountry')}</Label>
              <Input
                id="addr-country"
                maxLength={2}
                {...form.register('address.country', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  {...form.register('active')}
                />
                {t('suppliers.fields.active')}
              </label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate('/suppliers')}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isEdit ? t('common.save') : t('suppliers.form.createButton')}
          </Button>
        </div>
      </form>
    </div>
  );
}
